export type Status = 'Abgeschlossen' | 'In Produktion' | 'Bereit zum Verpacken'

export interface Product {
  id: string
  name: string
  productNumber: string
  batchCount: number
  statuses: Status[]
  lastProductionDate: string
}

export type Portal = 'ATC' | 'OSapiens'

export interface PortalTransferRecord {
  transferred: boolean
  transferredAt?: string // ISO or formatted date when transfer happened
}

export interface Batch {
  id: string
  productId: string
  batchNumber: string
  productionDate: string
  status: Status
  /** When the production was finished, e.g. "23.05.2026, 18:42". Only set when status === 'Abgeschlossen'. */
  completedAt?: string
  // Transfer status per portal
  transfers?: {
    ATC?: PortalTransferRecord
    OSapiens?: PortalTransferRecord
  }
  // Available fields used by the portal validation. Missing/empty values
  // are flagged as validation errors when the user tries to transfer.
  fields?: {
    productName?: string
    customer?: string
    orderNumber?: string
    quantity?: string
    mhd?: string
    materialCount?: number
    qsNumber?: string
  }
}

// Required fields per portal — used by the transfer modal to validate batches.
export const PORTAL_REQUIRED_FIELDS: Record<
  Portal,
  { key: keyof NonNullable<Batch['fields']>; label: string }[]
> = {
  ATC: [
    { key: 'productName', label: 'Produktname' },
    { key: 'mhd', label: 'MHD' },
    { key: 'materialCount', label: 'Material-Anzahl' },
    { key: 'qsNumber', label: 'QS-Nummer' },
  ],
  OSapiens: [
    { key: 'productName', label: 'Produktname' },
    { key: 'customer', label: 'Kunde' },
    { key: 'orderNumber', label: 'Bestellnummer' },
    { key: 'quantity', label: 'Menge' },
    { key: 'mhd', label: 'MHD' },
  ],
}

/** Returns the list of required field labels that are missing for the given batch + portal. */
export function getMissingFields(batch: Batch, portal: Portal): string[] {
  const required = PORTAL_REQUIRED_FIELDS[portal]
  return required
    .filter((f) => {
      const v = batch.fields?.[f.key]
      return v === undefined || v === null || v === ''
    })
    .map((f) => f.label)
}

export interface Material {
  id: string
  name: string
  batchNo: string
  supplier: string
  scan: string
  mhd: string
  we: string // Wareneingang
  quantity: string
}

export interface SubGroup {
  id: string
  label: string // e.g. "Räucherkammer 2" or "Palette 4920183"
  materials: Material[]
  /** Optional scan timestamp (used e.g. for Schälerei palette rows) */
  scan?: string
}

export interface OrderRelatedItem {
  id: string
  /** Identifier shown in the first column (e.g. Chargen-Nr., Rohstoff-Nr., Produkt-Nr.) */
  number: string
  /** Scan timestamp string, e.g. "21.05.2026, 09:01" */
  scan: string
}

export interface PackagingOrder {
  id: string
  customer: string
  orderNumber: string
  // Extra info shown in the details panel
  quantity?: string
  deliveryDate?: string
  packagingType?: string
  notes?: string
  // Drill-down lists shown as tabs inside the details modal
  chargen?: OrderRelatedItem[]
  rohstoffe?: OrderRelatedItem[]
  produkte?: OrderRelatedItem[]
}

export interface ProductionSection {
  id: string
  title: string
  iconName: 'Settings' | 'UtensilsCrossed' | 'Cloud' | 'Scissors' | 'Package'
  materials: Material[]
  /**
   * Optional sub-groups (e.g. multiple Räucherkammern or Paletten).
   * When present, the section is rendered as tabs by sub-group.
   */
  subGroups?: SubGroup[]
  /**
   * Optional packaging orders (Kunde / Bestellnummer rows with a details panel).
   * When present, the section is rendered as a packaging orders table.
   */
  packagingOrders?: PackagingOrder[]
  extraInfo?: string
}

export interface BatchDetail {
  batchId: string
  sections: ProductionSection[]
}

export const products: Product[] = [
  {
    id: 'p1',
    name: 'Mettenden 75g ITW',
    productNumber: '00012',
    batchCount: 12,
    statuses: ['Abgeschlossen', 'In Produktion'],
    lastProductionDate: '24.05.2026',
  },
  {
    id: 'p2',
    name: 'Käsebeisser',
    productNumber: '00018',
    batchCount: 8,
    statuses: ['Abgeschlossen'],
    lastProductionDate: '22.05.2026',
  },
  {
    id: 'p3',
    name: 'Pfefferb. pikant ITW',
    productNumber: '00024',
    batchCount: 15,
    statuses: ['Abgeschlossen', 'In Produktion', 'Bereit zum Verpacken'],
    lastProductionDate: '25.05.2026',
  },
  {
    id: 'p4',
    name: 'Schinkenmett. Pap. QS',
    productNumber: '00031',
    batchCount: 5,
    statuses: ['Bereit zum Verpacken'],
    lastProductionDate: '20.05.2026',
  },
  {
    id: 'p5',
    name: 'Snackinis hot QS',
    productNumber: '00042',
    batchCount: 22,
    statuses: ['Abgeschlossen', 'In Produktion', 'Bereit zum Verpacken'],
    lastProductionDate: '26.05.2026',
  },
  {
    id: 'p6',
    name: 'Salami Classic',
    productNumber: '00055',
    batchCount: 34,
    statuses: ['Abgeschlossen'],
    lastProductionDate: '18.05.2026',
  },
  {
    id: 'p7',
    name: 'Bierwurst',
    productNumber: '00063',
    batchCount: 7,
    statuses: ['In Produktion'],
    lastProductionDate: '27.05.2026',
  },
  {
    id: 'p8',
    name: 'Leberwurst fein',
    productNumber: '00071',
    batchCount: 19,
    statuses: ['Abgeschlossen', 'Abgeschlossen'],
    lastProductionDate: '21.05.2026',
  },
]

export const batches: Batch[] = [
  {
    id: 'b1',
    productId: 'p1',
    batchNumber: 'CH-2026-05-101',
    productionDate: '24.05.2026',
    status: 'In Produktion',
    transfers: {},
    fields: {
      productName: 'Mettenden 75g ITW',
      mhd: '15.08.2026',
      materialCount: 8,
      qsNumber: 'QS-4471-101',
      customer: 'EDEKA Nord',
      orderNumber: '26213755',
      quantity: '480 Stk.',
    },
  },
  {
    id: 'b2',
    productId: 'p1',
    batchNumber: 'CH-2026-05-098',
    productionDate: '23.05.2026',
    status: 'Abgeschlossen',
    completedAt: '23.05.2026, 18:15',
    transfers: {
      ATC: { transferred: true, transferredAt: '23.05.2026, 16:42' },
    },
    fields: {
      productName: 'Mettenden 75g ITW',
      mhd: '14.08.2026',
      materialCount: 8,
      qsNumber: 'QS-4471-098',
      customer: 'REWE Group',
      orderNumber: '26213758',
      quantity: '320 Stk.',
    },
  },
  {
    id: 'b3',
    productId: 'p1',
    batchNumber: 'CH-2026-05-085',
    productionDate: '20.05.2026',
    status: 'Bereit zum Verpacken',
    transfers: {
      ATC: { transferred: true, transferredAt: '20.05.2026, 18:10' },
      OSapiens: { transferred: true, transferredAt: '20.05.2026, 18:11' },
    },
    fields: {
      productName: 'Mettenden 75g ITW',
      mhd: '11.08.2026',
      materialCount: 7,
      qsNumber: 'QS-4471-085',
      customer: 'Kaufland',
      orderNumber: '26213512',
      quantity: '600 Stk.',
    },
  },
  {
    id: 'b4',
    productId: 'p2',
    batchNumber: 'CH-2026-05-090',
    productionDate: '22.05.2026',
    status: 'Abgeschlossen',
    completedAt: '22.05.2026, 17:42',
    transfers: {},
    fields: {
      productName: 'Käsebeisser',
      mhd: '13.08.2026',
      materialCount: 6,
      qsNumber: 'QS-4471-090',
      customer: 'EDEKA Süd',
      orderNumber: '26213601',
      quantity: '240 Stk.',
    },
  },
  {
    id: 'b5',
    productId: 'p3',
    batchNumber: 'CH-2026-05-105',
    productionDate: '25.05.2026',
    status: 'In Produktion',
    transfers: {},
    fields: {
      productName: 'Pfefferb. pikant ITW',
      mhd: '16.08.2026',
      materialCount: 9,
      qsNumber: 'QS-4471-105',
      quantity: '360 Stk.',
    },
  },
  {
    id: 'b6',
    productId: 'p1',
    batchNumber: 'CH-2026-05-076',
    productionDate: '18.05.2026',
    status: 'Abgeschlossen',
    completedAt: '18.05.2026, 18:55',
    transfers: {},
    fields: {
      productName: 'Mettenden 75g ITW',
      mhd: '09.08.2026',
      materialCount: 8,
      qsNumber: 'QS-4471-076',
      customer: 'ALDI Nord',
      orderNumber: '26213421',
      quantity: '540 Stk.',
    },
  },
  {
    id: 'b7',
    productId: 'p2',
    batchNumber: 'CH-2026-05-072',
    productionDate: '17.05.2026',
    status: 'Abgeschlossen',
    completedAt: '17.05.2026, 19:20',
    transfers: {},
    fields: {
      productName: 'Käsebeisser',
      mhd: '08.08.2026',
      materialCount: 6,
      qsNumber: 'QS-4471-072',
      customer: 'Lidl',
      orderNumber: '26213388',
      quantity: '420 Stk.',
    },
  },
  {
    id: 'b8',
    productId: 'p3',
    batchNumber: 'CH-2026-05-064',
    productionDate: '15.05.2026',
    status: 'Abgeschlossen',
    completedAt: '15.05.2026, 17:48',
    transfers: {},
    fields: {
      productName: 'Pfefferb. pikant ITW',
      mhd: '06.08.2026',
      materialCount: 9,
      qsNumber: 'QS-4471-064',
      customer: 'Kaufland',
      orderNumber: '26213240',
      quantity: '380 Stk.',
    },
  },
  {
    id: 'b9',
    productId: 'p1',
    batchNumber: 'CH-2026-05-058',
    productionDate: '13.05.2026',
    status: 'Abgeschlossen',
    completedAt: '13.05.2026, 16:55',
    transfers: {
      ATC: { transferred: true, transferredAt: '13.05.2026, 17:30' },
    },
    fields: {
      productName: 'Mettenden 75g ITW',
      mhd: '04.08.2026',
      materialCount: 8,
      qsNumber: 'QS-4471-058',
      customer: 'REWE Group',
      orderNumber: '26213195',
      quantity: '610 Stk.',
    },
  },
  {
    id: 'b10',
    productId: 'p2',
    batchNumber: 'CH-2026-05-051',
    productionDate: '11.05.2026',
    status: 'Abgeschlossen',
    completedAt: '11.05.2026, 18:10',
    transfers: {},
    fields: {
      productName: 'Käsebeisser',
      mhd: '02.08.2026',
      materialCount: 6,
      qsNumber: 'QS-4471-051',
      customer: 'EDEKA Nord',
      orderNumber: '26213102',
      quantity: '300 Stk.',
    },
  },
  {
    id: 'b11',
    productId: 'p3',
    batchNumber: 'CH-2026-05-044',
    productionDate: '09.05.2026',
    status: 'Abgeschlossen',
    completedAt: '09.05.2026, 17:30',
    transfers: {},
    fields: {
      productName: 'Pfefferb. pikant ITW',
      mhd: '31.07.2026',
      materialCount: 9,
      qsNumber: 'QS-4471-044',
      customer: 'tegut…',
      orderNumber: '26213044',
      quantity: '260 Stk.',
    },
  },
]

export const batchDetails: Record<string, BatchDetail> = {
  b1: {
    batchId: 'b1',
    sections: [
      {
        id: 's1',
        title: 'Kutterei',
        iconName: 'Settings',
        materials: [
          {
            id: 'm1',
            name: 'Schweinefleisch S III',
            batchNo: 'L-8821-A',
            supplier: 'Tönnies',
            scan: '21.05.2026, 09:01',
            mhd: '15.08.2026',
            we: '19.05.2026',
            quantity: '125,4 kg',
          },
          {
            id: 'm2',
            name: 'Rindfleisch R II',
            batchNo: 'L-8822-B',
            supplier: 'Dane Pork',
            scan: '21.05.2026, 08:10',
            mhd: '12.08.2026',
            we: '18.05.2026',
            quantity: '48,2 kg',
          },
          {
            id: 'm3',
            name: 'Speck',
            batchNo: 'L-8810-C',
            supplier: 'Tönnies',
            scan: '21.05.2026, 09:08',
            mhd: '20.08.2026',
            we: '19.05.2026',
            quantity: '35,0 kg',
          },
          {
            id: 'm4',
            name: 'Eis',
            batchNo: '-',
            supplier: 'Eigenproduktion',
            scan: '-',
            mhd: '-',
            we: '24.05.2026',
            quantity: '15,0 kg',
          },
        ],
      },
      {
        id: 's2',
        title: 'Füllerei',
        iconName: 'UtensilsCrossed',
        materials: [
          {
            id: 'm5',
            name: 'Nitritpökelsalz',
            batchNo: 'NPS-2026-01',
            supplier: 'Danisco',
            scan: '21.05.2026, 10:33',
            mhd: '01.12.2027',
            we: '10.01.2026',
            quantity: '4,5 kg',
          },
          {
            id: 'm6',
            name: 'Gewürzmischung Mettwurst',
            batchNo: 'GW-441-X',
            supplier: 'Werner Gewürze',
            scan: '21.05.2026, 10:34',
            mhd: '30.10.2026',
            we: '05.04.2026',
            quantity: '2,1 kg',
          },
          {
            id: 'm7',
            name: 'Pfefferkörner ganz',
            batchNo: 'PF-992',
            supplier: 'Reinert Gewürze',
            scan: '21.05.2026, 10:35',
            mhd: '15.11.2027',
            we: '12.02.2026',
            quantity: '0,8 kg',
          },
        ],
      },
      {
        id: 's3',
        title: 'Räucherei',
        iconName: 'Cloud',
        materials: [],
        subGroups: [
          {
            id: 'rk1',
            label: 'Räucherkammer 1',
            materials: [
              {
                id: 'm8a',
                name: 'Buchenholzspäne',
                batchNo: 'BH-2026-01',
                supplier: 'Holz Meier',
                scan: '22.05.2026, 11:20',
                mhd: '-',
                we: '01.03.2026',
                quantity: '8,5 kg',
              },
            ],
          },
          {
            id: 'rk2',
            label: 'Räucherkammer 2',
            materials: [
              {
                id: 'm8b',
                name: 'Buchenholzspäne',
                batchNo: 'BH-2026-01',
                supplier: 'Holz Meier',
                scan: '22.05.2026, 13:35',
                mhd: '-',
                we: '01.03.2026',
                quantity: '12,0 kg',
              },
            ],
          },
          {
            id: 'rk3',
            label: 'Räucherkammer 3',
            materials: [
              {
                id: 'm8d',
                name: 'Buchenholzspäne',
                batchNo: 'BH-2026-01',
                supplier: 'Holz Meier',
                scan: '22.05.2026, 15:50',
                mhd: '-',
                we: '01.03.2026',
                quantity: '10,2 kg',
              },
            ],
          },
          {
            id: 'rk4',
            label: 'Räucherkammer 4',
            materials: [
              {
                id: 'm8e',
                name: 'Buchenholzspäne',
                batchNo: 'BH-2026-01',
                supplier: 'Holz Meier',
                scan: '22.05.2026, 17:15',
                mhd: '-',
                we: '01.03.2026',
                quantity: '9,8 kg',
              },
            ],
          },
        ],
      },
      {
        id: 's4',
        title: 'Schälerei',
        iconName: 'Scissors',
        materials: [],
        subGroups: [
          {
            id: 'pal1',
            label: 'Palette 4920183',
            scan: '22.05.2026, 14:12',
            materials: [
              {
                id: 'm9a',
                name: 'Verpackungsfolie PA/PE',
                batchNo: 'FOL-881',
                supplier: 'PackTech',
                scan: '22.05.2026, 14:12',
                mhd: '-',
                we: '20.04.2026',
                quantity: '1 Rolle',
              },
              {
                id: 'm9b',
                name: 'Etiketten Standard',
                batchNo: 'ET-2026-A',
                supplier: 'PackTech',
                scan: '22.05.2026, 14:13',
                mhd: '-',
                we: '15.04.2026',
                quantity: '500 Stk.',
              },
            ],
          },
          {
            id: 'pal2',
            label: 'Palette 4920184',
            scan: '22.05.2026, 14:45',
            materials: [
              {
                id: 'm9c',
                name: 'Verpackungsfolie PA/PE',
                batchNo: 'FOL-882',
                supplier: 'PackTech',
                scan: '22.05.2026, 14:45',
                mhd: '-',
                we: '20.04.2026',
                quantity: '1 Rolle',
              },
            ],
          },
          {
            id: 'pal3',
            label: 'Palette 4920185',
            scan: '22.05.2026, 15:10',
            materials: [
              {
                id: 'm9d',
                name: 'Karton 5kg',
                batchNo: 'KT-771',
                supplier: 'PackTech',
                scan: '22.05.2026, 15:10',
                mhd: '-',
                we: '18.04.2026',
                quantity: '120 Stk.',
              },
            ],
          },
          {
            id: 'pal4',
            label: 'Palette 4920186',
            scan: '22.05.2026, 15:42',
            materials: [
              {
                id: 'm9e',
                name: 'Karton 5kg',
                batchNo: 'KT-772',
                supplier: 'PackTech',
                scan: '22.05.2026, 15:42',
                mhd: '-',
                we: '18.04.2026',
                quantity: '120 Stk.',
              },
            ],
          },
        ],
      },
      {
        id: 's5',
        title: 'Verpackung',
        iconName: 'Package',
        materials: [],
        packagingOrders: [
          {
            id: 'po1',
            customer: 'EDEKA Nord',
            orderNumber: '26213755',
            quantity: '480 Stk.',
            deliveryDate: '23.05.2026',
            packagingType: 'Schlauchbeutel 75g',
            notes: 'Standard-Lieferung, Eigenmarke EDEKA',
            chargen: [
              { id: 'c1', number: '130901190526', scan: '21.05.2026, 09:01' },
              { id: 'c2', number: '230901190526', scan: '21.05.2026, 09:15' },
            ],
            rohstoffe: [
              { id: 'r1', number: 'L-8821-A', scan: '21.05.2026, 09:01' },
              { id: 'r2', number: 'L-8822-B', scan: '21.05.2026, 08:10' },
              { id: 'r3', number: 'L-8810-C', scan: '21.05.2026, 09:08' },
            ],
            produkte: [
              { id: 'p1', number: 'PR-4471-001', scan: '23.05.2026, 14:32' },
              { id: 'p2', number: 'PR-4471-002', scan: '23.05.2026, 14:35' },
            ],
          },
          {
            id: 'po2',
            customer: 'REWE Group',
            orderNumber: '26213758',
            quantity: '320 Stk.',
            deliveryDate: '24.05.2026',
            packagingType: 'Schlauchbeutel 75g',
            notes: 'Aktionsware KW21, Display-Karton',
            chargen: [
              { id: 'c3', number: '130901200526', scan: '22.05.2026, 10:22' },
              { id: 'c4', number: '230901200526', scan: '22.05.2026, 10:48' },
            ],
            rohstoffe: [
              { id: 'r4', number: 'L-8822-B', scan: '22.05.2026, 08:30' },
              { id: 'r5', number: 'NPS-2026-01', scan: '22.05.2026, 10:33' },
            ],
            produkte: [
              { id: 'p3', number: 'PR-4471-003', scan: '24.05.2026, 09:12' },
            ],
          },
        ],
      },
    ],
  },
}
